import axios from 'axios';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { PaginationType } from '../../types/utils/pagination.type';
import { RouteParamsType } from '../../types/utils/router-params.type';
import { additionalQueryParams, getDefaultOrganization } from '../../utils/utilities';
import { setShowModal, setModalContent } from '../utils/utils.slice';
import {
  CreateOrganizationDto,
  OrganizationDetailsType,
  OrganizationListItemType,
  OrganizationMapStatsType,
  OrganizationType,
  UpdateOrganizationDto,
} from '../../types/organization/organization.type';
import { CREDENTIAL_TYPES } from '../../utils/constants';
import { getCurrentUser } from '../auth/auth.action';
import { getDomainTheme, getOrganizationTheme } from '../shared/shared.action';
import { rejectWithApiError } from '../../utils/api';
import { RootReducerState } from '../../redux/Reducers';

export const postOrganizationDetails = createAsyncThunk<
  OrganizationDetailsType,
  { data: CreateOrganizationDto },
  { state: RootReducerState }
>('organizations/postOrganizationDetails', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<OrganizationDetailsType>(`${process.env.REACT_APP_API_URL}/organizations`, data);
    thunkAPI.dispatch(getCurrentUser({ init: false }));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating organization!' });
  }
});

export const getOrganizationsList = createAsyncThunk<
  PaginationType<OrganizationListItemType>,
  RouteParamsType & { controller?: AbortController },
  { state: RootReducerState }
>('organizations/getOrganizationsList/no-loader', async (data, thunkAPI) => {
  try {
    const { controller, ...params } = data;
    const additionalParams = additionalQueryParams(
      thunkAPI.getState().sharedReducer.selectedCompany,
      null,
      thunkAPI.getState().sharedReducer.includeSuborgs,
      thunkAPI.getState().sharedReducer.selectedCountry
    );
    const mergedParams = { ...params, ...additionalParams };
    const queryString = Object.keys(mergedParams)
      .map(key => key + '=' + mergedParams[key])
      .join('&');
    const response = await axios.get<PaginationType<OrganizationListItemType>>(
      `${process.env.REACT_APP_API_URL}/organizations/short-list${queryString ? `?${queryString}` : ''}`,
      { signal: controller?.signal }
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching organizations!' });
  }
});

export const getOrganizationDetails = createAsyncThunk<
  OrganizationDetailsType,
  { id: number },
  { state: RootReducerState }
>('organizations/getOrganizationDetails', async ({ id }, thunkAPI) => {
  try {
    const response = await axios.get<OrganizationDetailsType>(`${process.env.REACT_APP_API_URL}/organizations/${id}`);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching organization!' });
  }
});

export const patchOrganizationDetails = createAsyncThunk<
  {
    org: OrganizationType;
    alerts: {
      parentHasChanged: boolean;
    };
  },
  { id: number; data: UpdateOrganizationDto; disableFetching?: boolean },
  { state: RootReducerState }
>('organizations/patchOrganizationDetails', async ({ data, disableFetching, id }, thunkAPI) => {
  try {
    const response = await axios.patch<{
      org: OrganizationType;
      alerts: {
        parentHasChanged: boolean;
      };
    }>(`${process.env.REACT_APP_API_URL}/organizations/${id}`, data);

    const domainTheme = thunkAPI.getState().sharedReducer?.domainTheme;
    if (domainTheme && +domainTheme?.id === +id) thunkAPI.dispatch(getDomainTheme());

    const organizationTheme = thunkAPI.getState().sharedReducer?.organizationTheme;
    if (organizationTheme && +organizationTheme?.id === +id) thunkAPI.dispatch(getOrganizationTheme(+id));

    thunkAPI.dispatch(getCurrentUser({ init: false }));
    if (!disableFetching) thunkAPI.dispatch(getOrganizationDetails({ id }));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating organization!' });
  }
});

export const deleteOrganizationDetails = createAsyncThunk<
  OrganizationType,
  { id: number; fetchList: boolean },
  { state: RootReducerState }
>('organizations/deleteOrganizationDetails', async ({ id, fetchList }, thunkAPI) => {
  try {
    const response = await axios.delete<OrganizationType>(`${process.env.REACT_APP_API_URL}/organizations/${id}`);
    if (fetchList)
      thunkAPI.dispatch(getOrganizationsList(thunkAPI.getState().organizationsReducer.organizationsRouteParams));
    thunkAPI.dispatch(getCurrentUser({ init: false }));
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during deleting organization!' });
  }
});

export const getOrganizationMapStats = createAsyncThunk<
  OrganizationMapStatsType,
  undefined,
  { state: RootReducerState }
>('organizations/getOrganizationMapStats', async (params, thunkAPI) => {
  try {
    const { selectedCompany, selectedCountry, includeSuborgs } = thunkAPI.getState().sharedReducer;
    const { user } = thunkAPI.getState().authReducer;

    const response = await axios.get<OrganizationMapStatsType>(`${process.env.REACT_APP_API_URL}/organizations/stats`, {
      timeout: 15000,
      params: {
        country_id: selectedCountry?.id,
        org_id: getDefaultOrganization(selectedCompany, user)?.id,
        inc_sub: selectedCompany?.id === 'all' ? true : includeSuborgs,
      },
    });
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching organization map stats!' });
  }
});

export const patchOrganizationBobo = createAsyncThunk<
  void,
  { id: number; data: { bobo_enabled: boolean } },
  { state: RootReducerState }
>('organizations/patchOrganizationBobo', async ({ id, data }, thunkAPI) => {
  try {
    const response = await axios.patch<void>(`${process.env.REACT_APP_API_URL}/organizations/${id}/bobo`, data);
    thunkAPI.dispatch(getOrganizationDetails({ id }));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during changing organization!' });
  }
});

export const patchRemoveOrganizationCredentials = createAsyncThunk<
  void,
  { id: number | string; type: string },
  { state: RootReducerState }
>('organizations/patchRemoveOrganizationCredentials', async ({ id, type }, thunkAPI) => {
  try {
    const response = await axios.patch<void>(`${process.env.REACT_APP_API_URL}/organizations/${id}/removeCredentials`, {
      type,
    });
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during removing credentials!' });
  }
});

export const postVerifyOrganizationCredentials = createAsyncThunk<
  void,
  { id: number; type: string; data: unknown },
  { state: RootReducerState }
>('organizations/postVerifyOrganizationCredentials', async ({ id, data, type }, thunkAPI) => {
  try {
    let url = `${process.env.REACT_APP_API_URL}`;
    if (type === CREDENTIAL_TYPES.InHand) {
      url += `/organizations/${id}/verifyInhandCredentials`;
    } else if (type === CREDENTIAL_TYPES.Cradlepoint) {
      url += `/organizations/${id}/verifyCradlepointCredentials`;
    } else if (type === CREDENTIAL_TYPES.Pepwave) {
      url += `/organizations/${id}/verifyPepwaveCredentials`;
    } else if (type === CREDENTIAL_TYPES.SierraWireless) {
      url += `/organizations/${id}/verifySierraWirelessCredentials`;
    }
    const response = await axios.patch<void>(url, data);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Credentials are invalid!' });
  }
});

export const postFixOrganizationRevIo = createAsyncThunk<
  { success: boolean },
  { id: number },
  { state: RootReducerState }
>('organizations/postFixOrganizationRevIo', async ({ id }, thunkAPI) => {
  try {
    const response = await axios.post<{
      success: boolean;
    }>(`${process.env.REACT_APP_API_URL}/organizations/${id}/fix-revio`, undefined);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fixing organization Rev.io!' });
  }
});

export const postMultipleOrgFiles = createAsyncThunk<
  { success: boolean },
  { data: unknown; params: any; providedURL?: string },
  { state: RootReducerState }
>('organizations/postMultipleOrgFiles', async ({ data, params, providedURL }, thunkAPI) => {
  try {
    const qs = Object.keys(params)
      .map(key => key + '=' + params[key])
      .join('&');
    const response = await axios.post<{
      success: boolean;
    }>(`${process.env.REACT_APP_API_URL}${providedURL || '/org-files/uploadFiles'}?${qs}`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during uploading file!' });
  }
});

export const patchOrgFile = createAsyncThunk<
  { success: boolean },
  { id: number; data: unknown },
  { state: RootReducerState }
>('organizations/patchOrgFile', async ({ id, data }, thunkAPI) => {
  try {
    const response = await axios.patch<{
      success: boolean;
    }>(`${process.env.REACT_APP_API_URL}/org-files/${id}`, data);

    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during uploading file!' });
  }
});

export const deleteOrgFile = createAsyncThunk<{ success: boolean }, { id: number }, { state: RootReducerState }>(
  'organizations/deleteOrgFile',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.delete<{
        success: boolean;
      }>(`${process.env.REACT_APP_API_URL}/org-files/${id}`);

      thunkAPI.dispatch(setShowModal(false));
      thunkAPI.dispatch(setModalContent(null));

      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during deleting file!' });
    }
  }
);

export const postOrgNote = createAsyncThunk<{ success: boolean }, { data: unknown }, { state: RootReducerState }>(
  'organizations/postOrgNote',
  async ({ data }, thunkAPI) => {
    try {
      const response = await axios.post<{
        success: boolean;
      }>(`${process.env.REACT_APP_API_URL}/org-note/`, data);

      thunkAPI.dispatch(setShowModal(false));
      thunkAPI.dispatch(setModalContent(null));

      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating note!' });
    }
  }
);

export const patchOrgNote = createAsyncThunk<
  { success: boolean },
  { id: number; data: unknown },
  { state: RootReducerState }
>('organizations/patchOrgNote', async ({ id, data }, thunkAPI) => {
  try {
    const response = await axios.patch<{
      success: boolean;
    }>(`${process.env.REACT_APP_API_URL}/org-note/${id}`, data);

    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during changing note!' });
  }
});

export const deleteOrgNote = createAsyncThunk<{ success: boolean }, { id: number }, { state: RootReducerState }>(
  'organizations/deleteOrgNote',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.delete<{
        success: boolean;
      }>(`${process.env.REACT_APP_API_URL}/org-note/${id}`);

      thunkAPI.dispatch(setShowModal(false));
      thunkAPI.dispatch(setModalContent(null));

      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during deleting note!' });
    }
  }
);

export const postVerifyOrganization = createAsyncThunk<{ result: string }, { id: number }, { state: RootReducerState }>(
  'organizations/postVerifyOrganization',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.post<{
        result: string;
      }>(`${process.env.REACT_APP_API_URL}/organizations/${id}/verify`);
      thunkAPI.dispatch(getOrganizationDetails({ id }));
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during verifying organization!' });
    }
  }
);

export type CustomerOrganizationAgentAssignment = {
  customerOrganizationId: number;
  agents: Array<{
    agentOrganizationId: number;
    agentOrganizationName: string;
  }>;
};

export type CustomerOrgAssignmentCoverage = 'direct' | 'inherited';

export type CustomerOrgAssignmentAgent = {
  agentOrganizationId: number;
  agentOrganizationName: string;
  coverage?: CustomerOrgAssignmentCoverage;
};

export type CustomerOrgAssignment = {
  customerOrganizationId: number;
  agents: CustomerOrgAssignmentAgent[];
};

export type CustomerOrgAssignmentsResponse = {
  assignments: CustomerOrgAssignment[];
};

export const buildAgentPortalAgentNamesByOrganizationId = (
  assignments: CustomerOrgAssignment[] | undefined
): Record<number, string> => {
  if (!assignments?.length) {
    return {};
  }

  return assignments.reduce<Record<number, string>>((byOrgId, assignment) => {
    const names = assignment.agents
      .map(agent => agent.agentOrganizationName)
      .filter(Boolean)
      .join(', ');
    if (!names) {
      return byOrgId;
    }
    const isInherited = assignment.agents.some(agent => agent.coverage === 'inherited');
    byOrgId[assignment.customerOrganizationId] = isInherited ? `${names} (via parent)` : names;
    return byOrgId;
  }, {});
};

export const putCustomerOrganizationAgentAssignment = createAsyncThunk<
  CustomerOrganizationAgentAssignment,
  { customerOrganizationId: number; agentOrganizationIds: number[] },
  { state: RootReducerState }
>(
  'organizations/putCustomerOrganizationAgentAssignment',
  async ({ customerOrganizationId, agentOrganizationIds }, thunkAPI) => {
    try {
      const response = await axios.put<CustomerOrganizationAgentAssignment>(
        `${process.env.REACT_APP_API_URL}/agent-portal/customer-organizations/${customerOrganizationId}/agent-assignment`,
        { agentOrganizationIds }
      );
      return response.data;
    } catch (error) {
      return rejectWithApiError({
        thunkAPI,
        error,
        fallback: 'Error updating Agent Portal agent assignment!',
      });
    }
  }
);
